
import { useEffect, useMemo, useRef, useState } from 'react'

import { type IProgress } from 'components-ref/molecules'
import { StoryFullscreen } from 'components-ref/templates'
import { useGlobalVideo } from 'context/globalVideoContext'
import {
  STILL_DURATION_MS,
  getStillElapsedMs,
  pauseStillClock,
  resetStillClock,
  resumeStillClock
} from 'services/stories/storyStillClock/storyStillClock'
import { usePreviewStore } from 'store/previewStore'
import { type Story } from 'types/api/story'
import { type StoryActionType } from 'types/types'

type StoryPreviewModalProps = {
  stories: Story[]
  startIndex?: number
  withCloseButton?: boolean
  onClose: () => void
  isInViewport?: boolean
  homeFeed?: boolean
  userId?: string
  username?: string
  displayName?: string
  avatar?: string | null
}

export const StoryPreviewModal = ({
  stories,
  startIndex = 0,
  withCloseButton = true,
  onClose,
  isInViewport = true,
  homeFeed = false,
  userId,
  username,
  displayName,
  avatar
}: StoryPreviewModalProps) => {
  const safeStories = Array.isArray(stories) ? stories : []
  const hasStories = safeStories.length > 0
const { getVideoElement } = useGlobalVideo()
  const previewOpen = usePreviewStore(state => state.previewOpen)

  const requestedIndex = Math.max(
    0,
    Math.min(
      Number.isFinite(startIndex) ? startIndex : 0,
      Math.max(0, safeStories.length - 1)
    )
  )

  const [, setStoryInteraction] = useState<StoryActionType>(null)
  const [isPaused, setIsPaused] = useState(false)

  const [activeStoryIndex, setActiveStoryIndex] = useState(
    requestedIndex
  )
  
  const getStoryMediaId = (storyId: string) =>
    `${homeFeed ? 'story' : 'story-list'}:${storyId}`

  // Remember whether the modal was open on the previous render.
  const [wasPreviewOpen, setWasPreviewOpen] = useState(previewOpen)

  // Synchronize only when reopening.
  // React retries this render before committing the fullscreen UI,
  // so an old story cannot flash before the newly selected story.
  if (homeFeed && wasPreviewOpen !== previewOpen) {
    setWasPreviewOpen(previewOpen)

    if (previewOpen) {
      setActiveStoryIndex(requestedIndex)
      setIsPaused(false)
    }
  }

  const activeStoryIndexRef = useRef(activeStoryIndex)

  // Navigation callbacks always use the latest committed selection.
  activeStoryIndexRef.current = activeStoryIndex

  const progressList: IProgress[] = useMemo(
    () =>
      safeStories.map(story => ({
        id: story.id,
        duration: story.video_src
          ? (story.duration || 7)
          : 7,
        seen: !!story.seen
      })),
    [stories]
  )

  const safeActiveIndex = Math.max(
    0,
    Math.min(activeStoryIndex, safeStories.length - 1)
  )

  const currentStory = safeStories[safeActiveIndex]

  const canRun =
    hasStories &&
    !isPaused &&
    isInViewport &&
    (!homeFeed || previewOpen)


  useEffect(() => {
    if (homeFeed || !hasStories) return

    const initialStory = safeStories[requestedIndex]
    if (!initialStory) return

    if (initialStory.video_src) {
      const video = getVideoElement(
        getStoryMediaId(initialStory.id),
        initialStory.video_src
      )

      if (video) {
        video.currentTime = 0
      }
    } else {
      resetStillClock(initialStory.id)
    }
  }, [])

  // Video stories advance only when native playback ends.
  useEffect(() => {
    if (!canRun || !currentStory?.video_src) return

    const storyId = currentStory.id
    const video = getVideoElement(
      getStoryMediaId(storyId),
      currentStory.video_src
    )

    if (!video) return

    const handleEnded = () => {
      const index = activeStoryIndexRef.current

      if (safeStories[index]?.id !== storyId) return

      if (index >= safeStories.length - 1) {
        setIsPaused(true)
        return
      }

      const nextIndex = index + 1
      const nextStory = safeStories[nextIndex]

      if (nextStory && !nextStory.video_src) {
        resetStillClock(nextStory.id)
      }

      activeStoryIndexRef.current = nextIndex
      setActiveStoryIndex(nextIndex)
    }

    video.addEventListener('ended', handleEnded)

    return () => {
      video.removeEventListener('ended', handleEnded)
    }
  }, [
    canRun,
    currentStory?.id,
    currentStory?.video_src,
    getVideoElement,
    safeStories
  ])

  // Keep the existing preview store synchronized with fullscreen
  // navigation, but never write an outdated opening index.
  useEffect(() => {
    if (!homeFeed || !previewOpen || !isInViewport) return

    const store = usePreviewStore.getState()

    if (
      store.state.selectedPostType !== 'story' ||
      store.state.selectedSlide === safeActiveIndex
    ) {
      return
    }

    store.setState(prev => ({
      ...prev,
      selectedSlide: safeActiveIndex
    }))
  }, [
    safeActiveIndex,
    homeFeed,
    previewOpen,
    isInViewport
  ])

  // Photos and polls share their elapsed time with the inline feed.
  useEffect(() => {
    if (!currentStory || currentStory.video_src) return
    if (!canRun) return

    const storyId = currentStory.id

    resumeStillClock(storyId, 'fullscreen')

    let frameId = 0
    let finished = false

    const tick = () => {
      if (finished) return

      const elapsedMs = getStillElapsedMs(storyId)

      if (elapsedMs >= STILL_DURATION_MS) {
        finished = true

        pauseStillClock(storyId, 'fullscreen')

        const currentIndex = activeStoryIndexRef.current

        // Ignore callbacks belonging to a previously selected story.
        if (safeStories[currentIndex]?.id !== storyId) {
          return
        }

        if (currentIndex >= safeStories.length - 1) {
          setIsPaused(true)
          return
        }

        const nextIndex = currentIndex + 1
        const nextStory = safeStories[nextIndex]

        if (nextStory && !nextStory.video_src) {
          resetStillClock(nextStory.id)
        }

        activeStoryIndexRef.current = nextIndex

        setActiveStoryIndex(nextIndex)

        return
      }

      frameId = requestAnimationFrame(tick)
    }

    frameId = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(frameId)
      pauseStillClock(storyId, 'fullscreen')
    }
  }, [
    currentStory?.id,
    currentStory?.video_src,
    canRun,
    safeStories
  ])

  // Keep the selected index valid when the story count changes.
  useEffect(() => {
    if (!hasStories) return

    setActiveStoryIndex(prev => {
      const next = Math.max(
        0,
        Math.min(prev, safeStories.length - 1)
      )

      activeStoryIndexRef.current = next

      return next
    })
  }, [
    hasStories,
    safeStories.length
  ])

  const onForward = () => {
    if (!safeStories.length) return

    const currentIndex = activeStoryIndexRef.current

    if (currentIndex >= safeStories.length - 1) {
      setIsPaused(true)
      return
    }

    const nextIndex = currentIndex + 1
    const nextStory = safeStories[nextIndex]

    if (!nextStory) return

    // Manual forward navigation always restarts the destination story.
    if (nextStory.video_src) {
      const video = getVideoElement(
        getStoryMediaId(nextStory.id),
        nextStory.video_src
      )

      if (video) {
        video.currentTime = 0
      }
    } else {
      resetStillClock(nextStory.id)
    }
    activeStoryIndexRef.current = nextIndex
    setActiveStoryIndex(nextIndex)
  }

  const onBack = () => {
    if (!safeStories.length) return

    const currentIndex = activeStoryIndexRef.current

    if (currentIndex <= 0) {
      setIsPaused(true)
      return
    }

    const nextIndex = currentIndex - 1
    const nextStory = safeStories[nextIndex]

    if (!nextStory) return

    // Backward navigation always restarts the destination story.
    if (nextStory.video_src) {
      const video = getVideoElement(
        getStoryMediaId(nextStory.id),
        nextStory.video_src
      )

      if (video) {
        video.currentTime = 0
      }
    } else {
      resetStillClock(nextStory.id)
    }

    activeStoryIndexRef.current = nextIndex
    setActiveStoryIndex(nextIndex)
  }

  if (!hasStories || (homeFeed && !previewOpen)) {
    return null
  }

  const user = (safeStories[0] as any)?.user ?? null

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        height: '100vh',
        width: '100vw',
        zIndex: 9999
      }}
    >
    <StoryFullscreen
      story={currentStory}
      user={user}
      homeFeed={homeFeed}
      userId={userId}
      username={username}
      displayName={displayName}
      avatar={avatar}
      progressList={progressList}
      activeStoryIndex={safeActiveIndex}
      withCloseButton={withCloseButton && !homeFeed}
      onClose={onClose}
      isPaused={isPaused || !isInViewport}
      setIsPaused={setIsPaused}
      setAction={setStoryInteraction}
      onBack={onBack}
      onForward={onForward}
      onSwipeLeft={onBack}
      onSwipeRight={onForward}
      isInViewport={isInViewport}
    />
    </div>
  )
}

export default StoryPreviewModal
